import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Header from "../components/tscm/Header";
import Footer from "../components/tscm/Footer";
import Contact from "../components/tscm/Contact";
import { scrollToId } from "../lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "../constants/site";
import {
  Breadcrumb,
  BeforeYouCall,
  ProgrammeLinks,
  SubpageHero,
  Section,
  CoverList,
  NumberedList,
  StepList,
} from "./SubpageBlocks";

const LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Corporate TSCM inspection",
    description:
      "Corporate TSCM and bug sweep inspections of offices, boardrooms and executive spaces across India, with a written report. By Law Abiding Technology (LAT).",
    areaServed: "India",
    provider: { "@id": "https://tscmservices.in/#org" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tscmservices.in/" },
      { "@type": "ListItem", position: 2, name: "Corporate TSCM", item: "https://tscmservices.in/corporate-tscm" },
    ],
  },
];

export default function CorporateTscm() {
  return (
    <div id="top" className="min-h-screen bg-base-color font-body text-ink">
      <Seo
        title="Corporate TSCM Inspections in India | TSCM Services"
        description="Corporate TSCM and bug sweep inspections of offices, boardrooms and executive spaces across India, with a written report. By Law Abiding Technology (LAT)."
        path="/corporate-tscm"
        jsonLd={LD}
      />
      <Header />
      <main>
        <div className="site-x pt-6">
          <Breadcrumb current="Corporate TSCM" />
        </div>
        <SubpageHero
          tag="Corporate TSCM inspection · Reactive"
          lines={["A full examination after", "a leak, a suspicion,", "or a dispute."]}
          image="/media/corporate-team-conference-sweep.jpg"
          imageAlt="The LAT team at work in a conference room — spectrum analysis at the table, thermal and physical checks around the room"
        >
          <p>
            A corporate TSCM inspection, commonly called a bug sweep, examines
            the room, the furniture, the fixtures and the spectrum method by
            method. It ends in a written report a board can read. Findings are
            reported within the conditions and limits recorded at the time.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <button
              onClick={() => scrollToId("contact")}
              data-testid="subpage-request-button"
              className="rounded-full bg-violet px-6 py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
            >
              Request a confidential consultation
            </button>
            <a
              href={PHONE_TEL}
              data-testid="subpage-phone-link"
              className="text-sm text-sub underline decoration-hairline underline-offset-4 transition-colors hover:text-ink"
            >
              or call {PHONE_DISPLAY}
            </a>
          </div>
          <p className="mt-6 text-base leading-relaxed">
            A confidential discussion can be exposed through the room it takes
            place in, and an inspection is how that exposure is checked.
            Methods: spectrum analysis, non-linear junction detection, thermal
            imaging, optical detection and physical search (
            <Link to="/how-we-work#method" className="text-violet transition-colors hover:text-violeth" data-testid="hero-method-link">
              see the methods on the home page
            </Link>
            ).
          </p>
        </SubpageHero>

        <Section title="What it covers." wide>
          <CoverList
            items={[
              "Offices, cabins and boardrooms",
              "Residences and vehicles can be added to an engagement",
              "A written report on completion: findings and recommendations, with photographs and instrument captures where a finding is recorded.",
            ]}
          />
        </Section>

        <Section title="After the first doubt.">
          <NumberedList
            items={[
              "A board decision leaks before it is announced",
              "An unknown device or wire surfaces during renovation or maintenance",
              "A dispute, audit or investigation raises what a leak would cost",
              "You take over a new office, floor or cabin and want it verified before use",
            ]}
          />
        </Section>

        <Section title="How an engagement runs." wide>
          <StepList
            steps={[
              { bold: "A scoping call", rest: ": the venue, the areas to be inspected and the time we can attend." },
              { bold: "The examination", rest: ": rooms, furniture, fixtures, wiring and spectrum, method by method." },
              { bold: "If something is found", rest: ": we report it to you. What happens next is your decision, taken with your own counsel; there is no single fixed procedure." },
              { bold: "The report", rest: ": what was examined, what was found, what we recommend." },
            ]}
          />
        </Section>

        <Section title="What the report contains.">
          <p className="text-base leading-relaxed text-sub">
            An executive summary for the Board with an overall
            residual-exposure rating; each recorded finding with why it
            matters, our confidence, our recommendation, a suggested owner and
            timing, and supporting photographs and instrument captures; and
            technical appendices with the radio-frequency analysis and the
            evidence behind the findings.{" "}
            <a
              href="/specimen-report.pdf"
              download="TSCM-Services-Specimen-Report.pdf"
              className="text-violet transition-colors hover:text-violeth"
              data-testid="specimen-report-link"
            >
              Download a specimen report
            </a>
          </p>
        </Section>

        <Section title="What it does not do.">
          <p className="text-base leading-relaxed text-sub">
            An inspection confirms a space at a point in time. It does not
            guarantee that the space stays clear afterwards, and it reports
            only within the conditions and limits recorded at the time.
          </p>
        </Section>

        <BeforeYouCall />
        <ProgrammeLinks
          links={[
            { href: "/preventive-tscm", tag: "Before the meeting", title: "Preventive TSCM" },
            { href: "/live-meeting-monitoring", tag: "During the meeting", title: "Live meeting monitoring" },
            { href: "/#contact", tag: "Ongoing", title: "Ask about an annual contract" },
          ]}
        />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
